import { useMutation } from '@apollo/client';
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import { Bullets, CollateContainer, Container, Form, LinkWrapper } from './UniqueIdScreen.styled';
import { TextField } from 'components/v2/Form/TextField/TextField';
import { StepTrackerLayout } from 'components/v2/Layouts/StepTrackerLayout/StepTrackerLayout';
import { ActionButtons } from 'screens/ActivateMembership/components/ActionButtons/ActionButtons';
import { Collate } from 'screens/ActivateMembership/components/Collate/Collate';
import { CollateType } from 'screens/ActivateMembership/components/Collate/Collate.types';
import { pageUrl, RoutesPath } from 'utilities/pageUrl';

import { VALIDATE_PATIENT_ENTERPRISE_MEMBERSHIP } from './UniqueIdScreen.graphql';
import { ValidateMembershipResponse, ValidateMembershipVariables } from './UniqueIdScreen.types';

interface LocationState {
  code?: string | null;
  fnLnDobValidationFailed?: boolean;
}

export const UniqueIdScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [uniqueId, setUniqueId] = useState('');
  const [uniqueIdValidationFailed, setUniqueIdValidationFailed] = useState(false);
  const locationState = location?.state as LocationState;
  const { code } = locationState ?? { code: null };
  const { fnLnDobValidationFailed } =
    locationState ?? ({ fnLnDobValidationFailed: false } as LocationState);

  const [validateMembership, { loading }] = useMutation<
    ValidateMembershipResponse,
    ValidateMembershipVariables
  >(VALIDATE_PATIENT_ENTERPRISE_MEMBERSHIP, {
    onError: () => setUniqueIdValidationFailed(true),
    onCompleted: () =>
      navigate(RoutesPath.CONFIRM_MEMBERSHIP_ACTIVATION, {
        state: {
          code: code,
          uniqueId: uniqueId
        }
      })
  });

  if (!code) {
    navigate(RoutesPath.ENTER_MEMBERSHIP_ACCESS_CODE, { replace: true });
  }

  const onValidateMembership = async () => {
    if (!code) return;

    validateMembership({
      variables: {
        employerAccessCode: code,
        employeeAccessCode: uniqueId
      }
    });
  };

  return (
    <StepTrackerLayout
      title={'Verify my Eligibility'}
      stepNames={['Services', 'Location', 'Activate']}
      selectedStep={2}
      renderMainContent={() => {
        return (
          <Container>
            {fnLnDobValidationFailed && !uniqueIdValidationFailed && (
              <CollateContainer>
                <Collate
                  type={CollateType.ERROR}
                  title="We need additional information to verify your eligibility."
                  description="We kindly request you input your Unique User ID as provided by your employer in the
                designated field below. Thank you for your cooperation."
                />
              </CollateContainer>
            )}
            {uniqueIdValidationFailed && (
              <CollateContainer>
                <Collate
                  type={CollateType.ERROR}
                  title="Unfortunately, we couldn't verify your eligibility due to the following reasons:"
                  description=""
                  additionalDescription={
                    <Bullets>
                      <li>The Employee Access Code (Unique ID) you entered is invalid</li>
                      <li>
                        You haven&apos;t been granted eligibility by your employer yet. Please
                        contact our Benefits Team for further assistance{' '}
                        <Link to={{ pathname: pageUrl.messages({ categoryName: 'benefits' }) }}>
                          <LinkWrapper>here</LinkWrapper>
                        </Link>
                      </li>
                    </Bullets>
                  }
                />
              </CollateContainer>
            )}
            <Form>
              <TextField
                type="text"
                status="default"
                id="input-unique-id"
                placeholder="Enter your unique ID"
                label="UNIQUE ID"
                value={uniqueId}
                onChange={event => setUniqueId(event.currentTarget.value)}
              />
            </Form>
            <ActionButtons
              onBack={() => navigate(-1)}
              onForward={onValidateMembership}
              forwardDisabled={!uniqueId || loading}
              forwardText="Validate"
            />
          </Container>
        );
      }}
    />
  );
};

export default UniqueIdScreen;
